import React, { useState, useEffect, useMemo } from "react"
import * as uuid from "uuid"
import * as httpx from "httpx"
import * as authzc from "authzcached"
import * as layouts from "layouts"
import * as inputs from "inputs"
import * as sessions from "sessions"
import * as brands from "brands"
import * as api from "brandguard/api"
import * as predictions from "brandguard/predictions"
import * as bgimages from "brandguard/display/brandguard/images"
import * as bglayouts from "brandguard/components/layouts"
import * as bgapprovals from "brandguard/display/approval"
import * as bgscores from "brandguard/display/scores"
import { UploadSimple } from "brandguard/components"
import * as authz from "authz"
import classnames from "classnames"
import * as nav from "brandguard/components/navigation"
import * as scores from "brandguard/components/layouts/scores"
import * as debugx from "x/debugx"
import * as icons from "icons"
import { CancellablePromise } from "real-cancellable-promise"
import { Image } from "media"
import * as mediaapi from "media/api"
import { styledscroll } from "brandguard/components/layouts"
import { useSettings } from "accounts/settings"
import { State } from "accounts/api"
import styled from "@emotion/styled"
import { FeedbackPanel } from "brandguard/feedback"

const OverlayContainer = styled(layouts.containers.absolute)`
	flex-direction: column;
`
OverlayContainer.defaultProps = {
	display: "flex",
	top: "0",
	left: "0",
	width: "100%",
	height: "100%",
}

export default function Display(props: React.PropsWithChildren<unknown>): JSX.Element {
	const authzaccount = authzc.useCache((cached) => cached.meta)
	const permission = authzaccount.current
	const bearertoken = sessions.useToken()
	const brand = brands.caching.useCached()
	const profile = sessions.useProfile()
	const settings = useSettings()
	const [loading, setLoading] = useState(true)
	const [selectedItem, setSelectedItem] = useState(undefined as api.ImageSearchResponseItem | undefined)
	const [uploadFocused, setUploadFocused] = useState(false)
	const [isRevising, setIsRevising] = useState(false)
	const [undoItems, setUndoItems] = useState<api.ImageSearchResponseItem[]>([])
	const [versionHistory, setVersionHistory] = useState<api.ImageSearchResponseItem[]>([])

	const overlay = bgimages.overlays.useCached()

	const [searchReq] = useState(
		api.images.uploads.searches.zero({
			offset: 0n,
			limit: 50n,
			brand_id: brand.id,
			queues: [uuid.NIL, brand.id],
			approved: [api.Prediction.APPROVED, api.Prediction.INREVIEW, api.Prediction.REJECTED],
		}),
	)

	const [images, setImages] = useState({
		items: [],
		next: searchReq,
	} as api.ImageSearchResponse)

	useEffect(() => {
		if (searchReq.brand_id === uuid.NIL) return
		if (!loading) setLoading(true)
		const retry = httpx.autoretry()
		const p = retry
			.wrap(() => api.images.uploads.search(searchReq.brand_id, searchReq, bearertoken))
			.then((r) => {
				setImages(r)
				setLoading(false)
			})
			.catch(httpx.errors.cancellation(() => undefined))
			.catch((cause) => {
				console.error("unable to display brandguard images", cause)
				setLoading(false)
			})
		return p.cancel
	}, [searchReq])

	useEffect(() => {
		if (images.items.length > 0) {
			if (selectedItem === undefined) setSelectedItem(images.items[0])
		} else {
			setSelectedItem(undefined)
		}
	}, [images])

	useEffect(() => {
		overlay.update(bgimages.overlays.zero({ event_id: selectedItem?.event?.id }))
	}, [selectedItem?.event?.id])

	useEffect(() => {
		if (!selectedItem) {
			setVersionHistory([])
			return
		}
		const p = api.images.uploads
			.versions(selectedItem.media!.id, brand.id, bearertoken)
			.then((resp) => setVersionHistory(resp.versions))
			.catch(httpx.errors.cancellation(() => undefined))
			.catch((cause) => console.warn("unable to fetch version history", cause))
		return p.cancel
	}, [selectedItem?.media?.id])

	const onImageUpdate = (item: api.ImageSearchResponseItem): void => {
		setImages((prevState) => ({
			...prevState,
			items: prevState.items.map((i) => (i.media?.id === item.media?.id ? item : i)),
		}))
	}

	const MemoUpload = useMemo(() => {
		return function MemodUpload(props: inputs.uploads.api.Props) {
			return (
				<authz.Protected
					enabled={permission.brandguard_upload}
					rejected={
						<layouts.containers.flex flex="1" className={classnames(bglayouts.uploads.navdenied)}>
							<authz.layouts.UploadDenied className="styled-area">
								You do not have permissions to upload new assets for scoring. Please contact an admin.
							</authz.layouts.UploadDenied>
						</layouts.containers.flex>
					}
				>
					<bgimages.uploads.default
						background={layouts.theme.colors.blue.blue}
						observation={api.Prediction.APPROVED}
						{...props}
					/>
				</authz.Protected>
			)
		}
	}, [])

	const nextImage = () => {
		setSelectedItem(images.items[(images.items.indexOf(selectedItem!) + 1) % images.items.length])
	}

	const prevImage = () => {
		setSelectedItem(images.items[(images.items.indexOf(selectedItem!) - 1 + images.items.length) % images.items.length])
	}

	const handleKeyDown = (evt: KeyboardEvent) => {
		if (evt.key === "ArrowRight") {
			nextImage()
		} else if (evt.key === "ArrowLeft") {
			prevImage()
		}
	}

	const agree = async (
		item: api.ImageSearchResponseItem & { event: api.ImageEvent },
	): Promise<api.ImageSearchResponseItem> =>
		api.images.approval.disagree(item, authzc.bearer(authzaccount)).then((res_i) => {
			setUndoItems([...undoItems, ...[item]])
			setImages((prevState) => ({
				...prevState,
				items: prevState.items.map((i) => (i.media?.id === res_i.media?.id ? res_i : i)),
			}))
			if (selectedItem && selectedItem.media?.id === res_i.media?.id) setSelectedItem(res_i)
			return res_i
		})

	const disagree = (
		item: api.ImageSearchResponseItem & { event: api.ImageEvent },
	): Promise<api.ImageSearchResponseItem> =>
		api.images.uploads
			.patch(
				item.event.brand_id,
				item.event.id,
				{
					...item,
					media: { ...item.media!, tombstoned_at: new Date().toISOString() },
					event: { ...item.event, observation: api.Prediction.DISCARDED_BY_UPLOADER },
				},
				authzc.bearer(authzaccount),
			)
			.then((res_i) => {
				setUndoItems([...undoItems, ...[item]])
				setImages((prevState) => ({
					...prevState,
					items: prevState.items.map((i) => (i.media?.id === res_i.media?.id ? res_i : i)),
				}))
				if (selectedItem && selectedItem.media?.id === res_i.media?.id) setSelectedItem(res_i)
				return res_i
			})

	const undo = (
		current: api.ImageSearchResponseItem & { event: api.ImageEvent },
		previously: api.ImageSearchResponseItem & { event: api.ImageEvent },
	): Promise<api.ImagePatchResponse> => {
		return api.images.uploads
			.patch(previously.event.brand_id, previously.event.id, previously, authzc.bearer(authzaccount))
			.then((resp_i) => {
				setUndoItems(undoItems.filter((i) => i.media?.id !== previously.media?.id))
				setImages((prevState) => ({
					...prevState,
					items: prevState.items.map((i) => (i.media?.id === previously.media?.id ? previously : i)),
				}))
				if (selectedItem && selectedItem.media?.id === previously.media?.id) setSelectedItem(previously)
				return resp_i
			})
			.then((upload) => {
				return api.images.approvallog
					.find(current.event.id, current.event.brand_id, bearertoken)
					.then((result) => {
						return api.images.approvallog
							.destroy(result.event!.brand_id, result.event!.id, authzc.bearer(authzaccount))
							.then(() => {
								return upload
							})
					})
					.catch(
						httpx.errors.notFound((cause) => {
							console.debug("unable to find approval images", cause)
							// ignore 404s
							return upload
						}),
					)
			})
	}

	useEffect(() => {
		if (!selectedItem) return
		window.addEventListener("keydown", handleKeyDown)
		return () => {
			window.removeEventListener("keydown", handleKeyDown)
		}
	}, [selectedItem])

	return (
		<layouts.containers.flex flexDirection="column-reverse" flex="1" mb="25px">
			<layouts.loading.pending className="brandguard-working-area" loading={loading} flex="1" overflow="hidden">
				<layouts.containers.flex className="center-panel" flex="1" flexDirection="column">
					{images.items.length === 0 && <bgscores.layouts.PreviewZero />}
					{selectedItem && (
						<layouts.containers.flex
							flex="1"
							flexDirection="row"
							border={layouts.theme.borders.grey.dark50alpha}
							boxShadow={layouts.theme.boxshadow.black.wide}
						>
							<layouts.containers.flex
								className="left-image-side"
								flex="6"
								borderRight={layouts.theme.borders.grey.dark50alpha}
								alignItems="center"
								justifyContent="center"
								background={layouts.theme.backgrounds.bluealpha5}
								position="relative"
							>
								<layouts.overlays.Screen enabled>
									<Image media={mediaapi.zero({ content_digest: selectedItem.media?.md5 })}>
										{overlay.items.map((o, i) => (
											<OverlayContainer key={o.uuid + i} zIndex={o.position}>
												{o.element}
											</OverlayContainer>
										))}
									</Image>
								</layouts.overlays.Screen>
								{images.items.length > 1 && (
									<>
										<layouts.containers.absolute left="-20px">
											<layouts.Flex
												background={`${layouts.theme.colors.white}BF`}
												boxShadow="0px 1px 2px 0px #00000040"
												alignItems="center"
												justifyContent="center"
												borderRadius="50%"
												width="40px"
												height="40px"
												cursor="pointer"
												onClick={prevImage}
											>
												<icons.arrows.ArrowLeft />
											</layouts.Flex>
										</layouts.containers.absolute>
										<layouts.containers.absolute right="-20px">
											<layouts.Flex
												background={`${layouts.theme.colors.white}BF`}
												boxShadow="0px 1px 2px 0px #00000040"
												alignItems="center"
												justifyContent="center"
												borderRadius="50%"
												width="40px"
												height="40px"
												cursor="pointer"
												onClick={nextImage}
											>
												<icons.arrows.ArrowRight />
											</layouts.Flex>
										</layouts.containers.absolute>
									</>
								)}
							</layouts.containers.flex>
							<layouts.containers.flex
								flex="5"
								py="10px"
								pl="40px"
								pr="20px"
								className={classnames("right-score-side", bgscores.layouts.styledscores)}
								background={layouts.theme.colors.white}
							>
								<layouts.Flex flex="1" minHeight="0px" height="calc(100vh - 250px)">
									<layouts.Flex flex="1" flexDirection="column" overflowY="auto" pr="10px" className={styledscroll}>
										<bgscores.layouts.accordion.Container initOpen title="Asset Scores">
											<bgimages.Scores item={selectedItem} />
										</bgscores.layouts.accordion.Container>
							{selectedItem.event && (
								<FeedbackPanel eventType="image" eventIds={[selectedItem.event.id]} />
							)}
										<bgapprovals.status.default>
											<bgapprovals.status.Uploads
												item={selectedItem}
												previtemstate={undoItems.find((u) => u.media?.id === selectedItem.media?.id)}
												agree={agree}
												disagree={disagree}
												undo={undo}
												borderRadius="10px"
											/>
										</bgapprovals.status.default>
										<bgscores.layouts.accordion.Container title="More Assets">
											<layouts.containers.ResponsiveGrid gap={10} breakpointCols={3} py="10px" px="5px">
												{images.items.map((item) => {
													return (
														<bgimages.Display
															cardClassName={classnames(bgscores.layouts.styledcardimage)}
															key={item.media?.id}
															item={item}
															active={selectedItem && item?.media?.id === selectedItem.media?.id}
															onCardClick={() => setSelectedItem(item)}
															onChange={onImageUpdate}
															panel={
																<authz.Protected enabled={settings.confidence !== State.DISABLED}>
																	<layouts.containers.absolute>
																		<scores.BGScoreImageSimple event={item.event!} />
																	</layouts.containers.absolute>
																</authz.Protected>
															}
															find={api.images.uploads.find}
														/>
													)
												})}
											</layouts.containers.ResponsiveGrid>
										</bgscores.layouts.accordion.Container>
										<authz.Protected enabled={debugx.alpha.enabled()}>
											<bgscores.layouts.accordion.Container title="Debug Info" textAlign="initial">
												<bgimages.Debug item={selectedItem} />
											</bgscores.layouts.accordion.Container>
										</authz.Protected>
										{versionHistory.length > 1 && (
											<bgapprovals.VersionHistory versions={versionHistory} current={selectedItem.media?.id || ""} onVersionClick={(v) => setSelectedItem(v as api.ImageSearchResponseItem)} />
										)}
										<layouts.containers.flex className="actions"></layouts.containers.flex>
										<authz.Protected enabled={permission.brandguard_upload}>
											<bgimages.MultiActions
												uploadItems={[selectedItem]}
												deleteAction={permission.brandguard_upload ? api.images.uploads.patch : undefined}
												onDelete={(results) => {
													setSelectedItem(undefined)
													setImages((prevState) => ({
														...prevState,
														items: prevState.items.filter(
															(i) => !results.map((el) => el.media!.id).includes(i.media!.id),
														),
													}))
												}}
											>
												<layouts.containers.flex
													className="revise-action"
													title="Revise"
													background={isRevising ? layouts.theme.colors.grey.dark50 + "40" : "transparent"}
													onClick={() => {
														setIsRevising(true)
														setUploadFocused(true)
													}}
												>
													<icons.UnstyledFileUpload width="18" height="18" />
												</layouts.containers.flex>
											</bgimages.MultiActions>
										</authz.Protected>
									</layouts.Flex>
								</layouts.Flex>
							</layouts.containers.flex>
						</layouts.containers.flex>
					)}
				</layouts.containers.flex>
			</layouts.loading.pending>
			<layouts.containers.flex flexDirection="column" mb="5px" zIndex="0" position="relative">
				<layouts.buttons.outline
					width="150px"
					height="40px"
					borderRadius="4px"
					onClick={() => setUploadFocused(!uploadFocused)}
					mb="5px"
				>
					<icons.Plus height="20px" width="20px" stroke={layouts.theme.colors.blue.blue} />
					<layouts.Span px="10px">Upload Assets</layouts.Span>
				</layouts.buttons.outline>
				<layouts.containers.flex display={uploadFocused ? "flex" : "none"} position="absolute" top="100%">
					<UploadSimple
						className={classnames(nav.layouts.slideDownAnimation)}
						isProcessing={(i: api.ImageSearchResponseItem) => predictions.processing(i.event?.approved)}
						onChange={(img) => {
							setImages((prevState) => ({ ...prevState, items: [...[img], ...prevState.items] }))
							setSelectedItem(img)
							setIsRevising(false)
						}}
						resume={() => {
							const searchReq = {
								offset: 0n,
								limit: 20n,
								uploaded_by: [profile.id],
								brand_id: brand.id,
								queues: [uuid.NIL, brand.id],
								approved: [api.Prediction.NONE],
							} as api.ImageSearchRequest

							return api.images.uploads.search(searchReq.brand_id, searchReq, bearertoken).then((imgs) => {
								return imgs.items.map(
									(i) =>
										({
											id: uuid.v4(),
											display: i.media?.description,
											background: layouts.theme.colors.blue.blue,
											data: i,
										} as inputs.uploads.api.uploaded<api.ImageSearchResponseItem>),
								)
							})
						}}
						refresh={(data) => {
							return api.images.uploads.find(data.media?.id!, brand.id, bearertoken).then((resp) => {
								return resp.event?.approved === api.Prediction.ERROR
									? CancellablePromise.reject(resp)
									: CancellablePromise.resolve(resp)
							})
						}}
						upload={(stats, content) =>
							isRevising
								? api.images.uploads.revise(content, brand.id, selectedItem!.media!.id, authzc.bearer(authzaccount))
								: api.images.uploads.create(content, brand.id, authzc.bearer(authzaccount))
						}
						DropWell={MemoUpload}
						onClose={() => {
							setUploadFocused(false)
							setIsRevising(false)
						}}
					/>
				</layouts.containers.flex>
			</layouts.containers.flex>
		</layouts.containers.flex>
	)
}
